/**
 * A module, as one card in a grid.
 *
 * The feed's card (`ModuleFeedEmbed`) is a whole row of the screen: a carousel a
 * reader scrolls, a description, two actions and a results row. A *grid* cell is
 * about a twelfth of that, so a module in a profile's tab needs a card of its own
 * — and this is it, sized and shaped exactly like the book card beside it.
 *
 * What it keeps, and what it drops, is the judgement:
 *
 *  - **Kept: the playlist itself.** Three of its capsules, as thumbnails, in
 *    order. A playlist drawn as a rectangle of text would be indistinguishable
 *    from a book in the same grid, and the one thing that makes a module a module
 *    is that it is a *row of other work*.
 *  - **Kept: what a run is worth** — how many capsules, and the coins — because
 *    that is the fact a reader chooses by, and it is already on the record.
 *  - **Dropped: the actions.** The card is one door, and it opens the module; the
 *    way to play it and the way to its results are on the module's own page, which
 *    is one press away. A grid cell with two buttons in it is a grid of buttons.
 *
 * It reaches the record exactly as the feed's card does — the post's back-reference
 * when the publisher wrote one, the author's own records when it did not — so a
 * module shows up in a grid in the same cases it shows up in a feed, and neither
 * card can be the one that works.
 */
import {useMemo} from 'react'
import {Platform, Pressable, StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {Plural, Trans, useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {isModulePost, moduleCardFacts, moduleRefOf} from '#/lib/modulePostCard'
import {type NavigationProp} from '#/lib/routes/types'
import {useModuleByPostQuery, useModuleByUriQuery} from '#/state/queries/module'
import {atoms as a, useTheme} from '#/alf'
import {Play_Filled_Corner0_Rounded as Play} from '#/components/icons/Play'
import {Text} from '#/components/Typography'
import {MODULE_ASPECT} from '#/components/work/types'
import {useModuleTiles} from './useModuleTiles'

export function ModulePostCard({
  post,
  module: moduleField,
  testID,
}: {
  post: {
    uri: string
    author: {did: string}
    record: unknown
  }
  /** The module, when the caller's own response carries one. */
  module?: Record<string, unknown>
  /** Named by whoever drives the grid, so two grids can be told apart. */
  testID?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const navigation = useNavigation<NavigationProp>()

  const postish = useMemo(
    () => ({record: post.record, module: moduleField}),
    [moduleField, post.record],
  )
  const ref = useMemo(() => moduleRefOf(postish), [postish])

  const {data: byUri} = useModuleByUriQuery(ref?.uri)
  const {data: byPost} = useModuleByPostQuery(
    ref ? '' : post.uri,
    ref ? '' : post.author.did,
  )
  const record = byUri ?? byPost ?? null

  const facts = useMemo(
    () => moduleCardFacts({post: postish, module: record}),
    [postish, record],
  )
  const {tiles} = useModuleTiles(facts.items)

  /*
   * Three capsules, thumbnails where they have them.
   *
   * Three because that is what fits legibly in a grid cell at phone width, and
   * because a playlist of six is a playlist of six whether the card can show them
   * all — the count beside the row is what says how many there are.
   */
  const preview = tiles.slice(0, 3)

  const onOpen = facts.moduleUri
    ? () => navigation.navigate('Module', {moduleUri: facts.moduleUri!})
    : undefined

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={l`Open the module ${facts.title}`}
      accessibilityHint={l`Opens the playlist of capsules`}
      disabled={!onOpen}
      onPress={onOpen}
      style={[
        a.flex_col,
        // A card with nowhere to go does not pretend to be pressable.
        onOpen && Platform.OS === 'web' ? {cursor: 'pointer' as const} : null,
      ]}>
      <View
        style={[
          styles.card,
          a.rounded_md,
          a.overflow_hidden,
          t.atoms.bg_contrast_25,
          t.atoms.border_contrast_low,
        ]}>
        {/* The playlist, as the shape of itself. */}
        <View style={[a.flex_1, a.flex_row, a.gap_xs, a.p_xs, a.pt_xs]}>
          {preview.length > 0 ? (
            preview.map(tile => (
              <View
                key={tile.key}
                style={[styles.slot, t.atoms.bg_contrast_50]}>
                {tile.coverUri ? (
                  <Image
                    source={{uri: tile.coverUri}}
                    style={[a.w_full, a.h_full]}
                    contentFit="cover"
                    accessibilityIgnoresInvertColors
                  />
                ) : (
                  <Text style={[a.text_xs, t.atoms.text_contrast_low]}>
                    {tile.position}
                  </Text>
                )}
              </View>
            ))
          ) : (
            /* The record is still on its way: the row keeps its shape. */
            <>
              <View style={[styles.slot, t.atoms.bg_contrast_50]} />
              <View style={[styles.slot, t.atoms.bg_contrast_50]} />
              <View style={[styles.slot, t.atoms.bg_contrast_50]} />
            </>
          )}
        </View>

        {/* What it is, and what a run is worth. */}
        <View style={[a.px_sm, a.pb_sm, a.pt_xs, a.gap_xs]}>
          {/*
            The facts wrap rather than shrink.

            A grid cell is a fifth of the width the feed's card gets, and *Playlist
            · 6 capsules · 18 coins to win* does not fit on one line of it: with
            `flex: 1` on the coins the row squeezed the tail to nothing and the
            card read **· 1…**, which is worse than saying less. The coins move to
            a line of their own instead — the number is a fact about the playlist,
            not a decoration, so it is kept rather than dropped.
          */}
          <View style={[a.flex_row, a.align_center, a.flex_wrap, a.gap_xs]}>
            <Play size="xs" fill={t.palette.primary_500} />
            <Text
              numberOfLines={1}
              style={[a.text_xs, a.font_bold, {color: t.palette.primary_500}]}>
              {facts.capsuleCount != null ? (
                <Plural
                  value={facts.capsuleCount}
                  one="Playlist · # capsule"
                  other="Playlist · # capsules"
                />
              ) : (
                <Trans>Playlist</Trans>
              )}
            </Text>
            {facts.coins != null && facts.coins > 0 ? (
              <Text
                numberOfLines={1}
                style={[a.text_xs, t.atoms.text_contrast_medium]}>
                ·{' '}
                <Plural
                  value={facts.coins}
                  one="# coin to win"
                  other="# coins to win"
                />
              </Text>
            ) : null}
          </View>
          <Text
            emoji
            numberOfLines={2}
            style={[
              a.text_sm,
              a.font_semi_bold,
              a.leading_tight,
              t.atoms.text,
            ]}>
            {facts.title}
          </Text>
        </View>
      </View>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    aspectRatio: MODULE_ASPECT,
    borderWidth: StyleSheet.hairlineWidth,
  },
  slot: {
    flex: 1,
    borderRadius: 6,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
})

/** Whether this post is a module, for a caller that wants one question. */
export {isModulePost as isModuleCard}
